/**
 * Keeping the work.
 *
 * One subscription, a debounce with a ceiling, and three checkpoints the user
 * never sees. The rule that matters: a session is written on `session` object
 * identity, not on `rev` — two store actions replace the session without
 * bumping the revision, and a rev-keyed writer would silently lose every
 * realised bridge and every token count.
 */

import { forkFamilyLabel, useStore } from './store'
import { sharedExplorationCopy } from './share'
import { setPaidCallDurabilityGuard } from './durability'
import { forgottenElsewhere, markMoved, markNow } from './forgotten'
import { ModelError } from '../engine/providers/contract'
import { mergeSpend, projectLabel, spentNothing } from './types'
import { retainNarration } from './narrationReceipt'
import type { Op, Spend } from './types'
import { nameFor } from './name'
import {
  activeDocumentId,
  checkpointRecoveryMessage,
  hydrateStorage,
  writeCheckpoint,
  persistedCheckpointRecovery,
  cacheDurableDocument,
  durableWritesEnabled,
  publishDurableDocument,
  readStoredSessionJson,
  storedSessionJson,
  setDurableWriter,
  DEMO_SESSION_ID,
  SCHEMA,
  loadSession,
  onWriteRefused,
  nameAdoption,
  projectAdoption,
  refreshFromIndexEvent,
  writeDoc,
  writeLast,
  writePrefs,
  type Prefs,
  type StoredSession,
} from './persist'
import {
  writeDurable,
  activateDurableDocument,
  durableDocumentIds,
  readDurableActiveId,
  readDurableRecord,
  DurableWriteError,
  type BuiltDocument,
} from './archive'

import {
  docIds,
  docVersion,
  documentsHydrated,
  readDocText,
  storageHydrationFailure,
  shelfOpen,
} from './docStore'
import { unpackText } from './pack'
import { packTextOffThread } from './packHost'

type StoreState = ReturnType<typeof useStore.getState>

/**
 * The drain adds one node per beat, so at the slowest tempo the debounce fires
 * between arrivals — roughly one write per node, a fraction of a millisecond
 * each. At the fastest tempo the gap is shorter than the debounce and it would
 * never expire, which is what the ceiling is for.
 */
const DEBOUNCE_MS = 1200
const CEILING_MS = 8000

function toPrefs(s: StoreState): Prefs {
  return {
    v: SCHEMA,
    lens: s.lens,
    temper: s.temper,
    tempo: s.tempo,
    filters: s.filters,
    railRight: s.railRight,
    // The reader's own tab, not the Overview a copy read first is showing: a
    // link must not change where their own explorations open after a reload.
    rightTab: s.readerTab ?? s.rightTab,
    preferCorpus: s.preferCorpus,
    splashSeen: s.splashSeen,
    viewMode: s.viewMode,
    weighting: s.weighting,
    instruments: s.instruments,
    showRings: s.showRings,
    dimUnrelated: s.dimUnrelated,
    showMinimap: s.showMinimap,
    sidebarCollapsed: s.sidebarCollapsed,
    threadWidth: s.threadWidth,
    sidebarWidth: s.sidebarWidth,
    foldedProjects: s.foldedProjects,
    openedAt: s.openedAt,
    // The SELECTED MODEL is deliberately not here. It lives in `nv.model`,
    // written synchronously by `setModel`, because `loadPrefs` returns null for
    // this whole blob on a parse failure — for a lens that is a taste reset, for
    // the model it is a silent change in what a source check can verify.
  }
}

// Hand-written, one field per line, and every field of `toPrefs` that can change
// has to appear: a preference the writer serialises but this comparison does not
// name is a preference that is written only when something ELSE happens to
// change, which reads as "it forgets sometimes" and is untraceable from outside.
function prefsChanged(a: StoreState, b: StoreState): boolean {
  if (
    a.lens !== b.lens ||
    a.temper !== b.temper ||
    a.tempo !== b.tempo ||
    a.railRight !== b.railRight ||
    a.rightTab !== b.rightTab ||
    a.readerTab !== b.readerTab ||
    a.preferCorpus !== b.preferCorpus ||
    a.splashSeen !== b.splashSeen ||
    a.viewMode !== b.viewMode ||
    a.weighting !== b.weighting ||
    a.instruments !== b.instruments ||
    a.showRings !== b.showRings ||
    a.dimUnrelated !== b.dimUnrelated ||
    a.showMinimap !== b.showMinimap ||
    a.sidebarCollapsed !== b.sidebarCollapsed ||
    a.threadWidth !== b.threadWidth ||
    a.sidebarWidth !== b.sidebarWidth ||
    // By identity, like `filters`: the store makes a new array on every fold,
    // and a new map on every opening.
    a.foldedProjects !== b.foldedProjects ||
    a.openedAt !== b.openedAt
  ) {
    return true
  }
  return a.filters !== b.filters
}

function writeCheckpointForState(s: StoreState): void {
  if (!s.session.order.length) return
  writeCheckpoint(
    {
      v: SCHEMA,
      savedAt: Math.max(
        Date.now(),
        (scheduledVersions.get(s.session.id) ?? committedVersions.get(s.session.id) ?? 0) + 1,
      ),
      clockAtSave: Date.now(),
      session: s.session,
      ui: { selectedId: s.selectedId, focusId: s.focusId, lens: s.lens, temper: s.temper },
    },
    committedVersions.get(s.session.id) ?? null,
  )
}

function writeState(s: StoreState, opts: { checkpoint?: boolean } = {}) {
  if (durableWritesEnabled()) {
    if (opts.checkpoint) writeCheckpointForState(s)
    queueState(s)
    return
  }
  const ses = s.session
  // A bare seed or a cleared session. Persisting the latter would resurrect an
  // empty session on boot and suppress the demo forever.
  if (ses.order.length <= 1) return
  const doc: StoredSession = {
    v: SCHEMA,
    savedAt: Date.now(),
    session: ses,
    ui: { selectedId: s.selectedId, focusId: s.focusId, lens: s.lens, temper: s.temper },
  }
  const wrote = writeDoc(doc, opts)
  if (wrote.ok) {
    void wrote.landed?.then((kept) => {
      if (!kept) {
        sayRefused(useStore.getState())
        return
      }
      refused = false
      if (useStore.getState().session.id === ses.id) {
        writeLast(ses.id)
        useStore.setState({ savedAt: Date.now(), saveState: 'saved' })
      }
    })
    return
  }
  // A DIFFERENT refusal, and it needs its own sentence. The exploration was
  // forgotten in another tab, so this one is holding something the user has
  // already deleted. Writing it back is the resurrection `core/forgotten.ts`
  // exists to stop — but the storage-full sentence would be a lie, and it ends
  // by sending them to free space they are not short of. Said every time rather
  // than once, because unlike a full browser this does not clear itself and each
  // attempt is another edit that is not being kept.
  if (wrote.refused === 'forgotten') {
    s.toast(
      'This exploration was forgotten in another tab, so it is no longer being saved here. Export it if you want to keep what is on screen.',
      'bad',
    )
    return
  }
  // The index row could not be written: the origin's small keys are full. Rare
  // now that the documents are on the shelf, and the same sentence as a shelf
  // refusal — see `sayRefused`.
  sayRefused(s)
}

/** A failed or pending document stays in memory until its own commit succeeds.
 * Navigation must not discard recoverable work from an outgoing exploration. */
const recoverable = new Map<string, StoredSession>()
const committedVersions = new Map<string, number>()
const scheduledVersions = new Map<string, number>()
const readEpochs = new Map<string, number>()
const recoveryForks = new Map<string, StoredSession>()
const workingBirths = new Map<string, number>()
const queuedSessions = new Map<string, StoreState['session']>()
let writes: Promise<void> = Promise.resolve()
let storageFailure: string | null = null
/**
 * The explorations this tab is deleting right now — `'*'` for all of them — and
 * what their saves met meanwhile, held until the deletion answers (D331).
 *
 * A save of an exploration being deleted is in an odd place: the document stands
 * until the shelf commits the deletion, so a save can be queued against it — an
 * answer about it finishing, a rename — and that save then meets the tombstone
 * and fails. Reported, that failure paused paid work for the rest of the tab over
 * a document the reader had asked to be rid of, with a sentence about another tab.
 * And a checkpoint queued just before the press used to be dropped as superseded
 * by this tab's own deletion — which is only true once the deletion is accepted.
 *
 * So while a deletion is in flight both are HELD: a failure is kept unreported,
 * and a superseded body is kept in `recoverable`. When the deletion answers
 * (`endForgetting`), an acceptance releases them — the save failed because what
 * it saved is gone — and a refusal reports the failures it would have reported
 * and saves the held bodies again, so the exploration the refusal kept is the one
 * the reader last saw.
 */
const deleting = new Set<string>()
const heldBodies = new Set<string>()
const heldFailures = new Map<string, string>()
/**
 * What an ACCEPTED deletion removed while another was still in flight, so the one
 * that answers later cannot report or re-save it. A single Forget and a
 * Forget-everything can overlap; whichever is accepted, what it removed is gone,
 * and a refusal of the other must not bring it back into the save queue or say a
 * save of it failed. Emptied when nothing is deleting.
 *
 * By id, with the moment it was accepted, and never as "everything": an accepted
 * Forget-everything removed what stood when it was accepted, and an exploration
 * begun after it — or the worked example replayed under its one reused id — is
 * not that, which its birth says (`workingBirths`). Held as `'*'`, it made a later
 * exploration's refused deletion skip that exploration's held saves as removed,
 * and the work the refusal kept was never saved again.
 */
const goneMeanwhile = new Map<string, number>()
/**
 * Versions a queued save was scheduled at and never wrote — held by a deletion in
 * flight. Every save expects the version of the one queued before it, so a save
 * behind a held one expected a version that never reached the shelf and failed as
 * a conflict, retry included: a conflict the hold caused, reported in a sentence
 * about another tab. Such a save expects what this tab last committed instead,
 * which still conflicts if another tab really did save something newer.
 */
const unwrittenVersions = new Map<string, Set<number>>()
const isDeleting = (id: string): boolean => deleting.has(id) || deleting.has('*')
let hydrationComplete = false
const failedDocuments = new Map<string, string>()

const storageMessage = (error: unknown): string =>
  error instanceof DurableWriteError && error.reason === 'conflict'
    ? 'Another tab saved a newer version. Export this copy before reopening the newer exploration.'
    : error instanceof DurableWriteError && error.reason === 'forgotten'
      ? 'This exploration was forgotten in another tab. Export this copy to keep the work on screen.'
      : 'This browser could not save your work. Paid work is paused. Retry saving or export a recovery copy.'

/**
 * Each queued document's stored form — its JSON and the packed form of that JSON —
 * built once, when it is queued.
 *
 * The working mirror, the durable write, the publication after it and a re-mirror
 * of the same document after a failure all want the same bytes, and each used to
 * build them again: on the 12 MB evidence fixture every autosave serialised and
 * deflated twice (three times when an edit landed during the write), which was most
 * of the save's own freeze. Keyed by the document object `queueDocument` creates —
 * nothing mutates it after — so a hit is the same bytes rebuilding would give.
 * Nothing about durability moves: the write still reads its text back through the
 * codec before it commits, and success still waits on the awaited transaction.
 *
 * The JSON is built here, on the page, because the session is only here; the
 * packing is started here and runs in a worker (`packHost.ts`), because it was the
 * rest of the freeze — 0.82 of 0.85 s per edit on that fixture. The write awaits it.
 */
const builds = new WeakMap<StoredSession, BuiltDocument>()
function buildOf(doc: StoredSession): BuiltDocument {
  let held = builds.get(doc)
  if (!held) {
    const text = storedSessionJson(doc)
    held = { text, packed: packTextOffThread(text) }
    builds.set(doc, held)
  }
  return held
}
function mirror(doc: StoredSession): void {
  const { text, packed } = buildOf(doc)
  cacheDurableDocument(doc, text, packed)
}

/** A save that failed, said: the document stays recoverable and on the mirror,
 *  paid work pauses, and the reader is told. */
function reportFailure(id: string, message: string): void {
  storageFailure = message
  failedDocuments.set(id, message)
  queuedSessions.delete(id)
  const unsaved = recoverable.get(id)
  if (unsaved) mirror(unsaved)
  useStore.setState({ saveState: 'failed', saveError: message })
  useStore.getState().toast(message, 'bad')
}

/** A deletion of this exploration — `null` for all of them — is starting. See
 *  `deleting`. Called by the personal remover in `core/forget.ts`. */
export function beginForgetting(id: string | null): void {
  deleting.add(id ?? '*')
}

/**
 * The deletion answered. `gone` when the exploration no longer stands for this
 * tab — accepted by the shelf, or forgotten from this page where the shelf could
 * not be read — and its held saves are released with its recovery copies: a
 * recovery copy kept for a page-only forget is a body the next retry would save
 * straight back, which is the forget undone. Otherwise the refusal kept it, and
 * the bodies held for it are saved again; a failure its saves met is retried
 * with them, or reported where there is nothing left to retry.
 */
export function endForgetting(id: string | null, gone: boolean): void {
  const key0 = id ?? '*'
  deleting.delete(key0)
  // What this answer removed, if it was accepted: its exploration, or every one.
  const coveredHere = (key: string) => gone && (id === null || key === id)
  // Recorded for the deletions still in flight, whose keys this answer cannot
  // settle: an accepted Forget-everything removed each of theirs too.
  if (gone) {
    const at = Date.now()
    for (const key of id === null ? deleting : [id]) goneMeanwhile.set(key, at)
  }
  const removed = (key: string) => {
    if (coveredHere(key)) return true
    const at = goneMeanwhile.get(key)
    const born = workingBirths.get(key)
    return at !== undefined && (born === undefined || born < at)
  }
  // A key is settled only once nothing else is deleting it, so the last answer
  // about a key is the one that acts — and an answer never acts on what an
  // overlapping deletion that was accepted has already removed (`removed`).
  const settles = (key: string) => (id === null || key === id) && !isDeleting(key)
  const retry = new Set<string>()
  for (const key of [...heldBodies]) {
    if (!settles(key)) continue
    heldBodies.delete(key)
    if (!removed(key)) retry.add(key)
  }
  for (const [key, message] of [...heldFailures]) {
    if (!settles(key)) continue
    heldFailures.delete(key)
    if (removed(key)) continue
    // A failure with a body still to save is retried rather than said: the hold
    // itself can cause one — a save queued behind a held body expects a version
    // that body never wrote — and the retry is the honest test of it. A retry
    // that fails reports in its own words.
    if (recoverable.has(key)) retry.add(key)
    else reportFailure(key, message)
  }
  for (const key of retry) {
    const body = recoverable.get(key)
    if (body) queueDocument(body)
  }
  if (gone) releaseForgotten(id)
  if (deleting.size === 0) goneMeanwhile.clear()
}

function queueDocument(input: StoredSession, keepPlace = false): Promise<boolean> {
  const id = input.session.id
  const generation = markNow(id)
  if (
    id === DEMO_SESSION_ID &&
    workingBirths.has(id) &&
    workingBirths.get(id) !== input.session.createdAt &&
    loadSession(id) === null
  ) {
    // The worked example is the sole deliberate id reuse. A fresh replay after
    // Forget has a new birth and no working copy; the native tombstone checks
    // its post-forget generation before admitting it.
    committedVersions.delete(id)
    scheduledVersions.delete(id)
    readEpochs.set(id, (readEpochs.get(id) ?? 0) + 1)
  }
  workingBirths.set(id, input.session.createdAt)
  const readEpoch = readEpochs.get(id) ?? 0
  // A queued edit is based on this read (or this tab's preceding queued save),
  // never on a version learned later by reopening the document in another turn.
  const expected = scheduledVersions.get(id) ?? committedVersions.get(id) ?? null
  const doc = {
    ...input,
    // The wall clock is the floor for a save somebody made. A write that keeps
    // its place (`writeDoc`'s `keepPlace`) still rises past every version this
    // tab knows, and no further: `savedAt` is also the row's place in Recent.
    savedAt: Math.max(keepPlace ? 0 : Date.now(), input.savedAt, (expected ?? 0) + 1),
    clockAtSave: input.clockAtSave ?? Date.now(),
  }
  scheduledVersions.set(id, doc.savedAt)
  recoverable.set(id, doc)
  // Synchronous readers must compose the next edit from this working body,
  // even before the queue starts. Its version is pending, not a durable receipt:
  // committedVersions and the awaited transaction alone authorize that claim.
  mirror(doc)
  writes = writes.then(async () => {
    useStore.setState({ saveState: 'saving' })
    try {
      if (markMoved(id, generation)) {
        if (!forgottenElsewhere(id)) {
          // This tab is deleting the document and queued this checkpoint first.
          // Only an ACCEPTED deletion supersedes it; while the deletion is in
          // flight the body is held, to be saved again if it is refused.
          if (isDeleting(id)) {
            heldBodies.add(id)
            const unwritten = unwrittenVersions.get(id) ?? new Set<number>()
            unwritten.add(doc.savedAt)
            unwrittenVersions.set(id, unwritten)
            return
          }
          // This tab deliberately deleted the document after queuing its final
          // checkpoint. Its own deletion supersedes that checkpoint.
          if (recoverable.get(id) === doc) recoverable.delete(id)
          failedDocuments.delete(id)
          storageFailure = failedDocuments.values().next().value ?? null
          return
        }
        throw new DurableWriteError('forgotten')
      }
      if ((readEpochs.get(id) ?? 0) !== readEpoch) throw new DurableWriteError('conflict')
      const built = buildOf(doc)
      const basis =
        expected !== null && unwrittenVersions.get(id)?.has(expected)
          ? (committedVersions.get(id) ?? null)
          : expected
      const text = await writeDurable(doc, basis, built)
      unwrittenVersions.delete(id)
      // Settled already: the write above waited for it.
      const packed = await built.packed
      committedVersions.set(id, doc.savedAt)
      if (!publishDurableDocument(doc, text, doc.savedAt, packed))
        throw new DurableWriteError('unavailable')
      const newer = recoverable.get(id)
      if (newer && newer !== doc) mirror(newer)
      // Only the active exploration controls the pointer. A late outgoing save
      // cannot redirect the next boot away from the user's newer navigation.
      const live = useStore.getState()
      if (live.session.id === id) {
        await activateDurableDocument(id, doc.savedAt)
        // Legacy builds may still read this hint; it never controls success.
        writeLast(id)
      }
      if (recoverable.get(id) === doc) recoverable.delete(id)
      failedDocuments.delete(id)
      // A failure an earlier save met while a deletion held it is over too: this
      // whole document is on the shelf now, and a refusal reporting it would pause
      // paid work over a save nothing is missing from.
      heldFailures.delete(id)
      storageFailure = failedDocuments.values().next().value ?? null
      if (live.session.id === id) {
        useStore.setState({
          savedAt: Date.now(),
          saveState: storageFailure
            ? 'failed'
            : live.session === input.session
              ? 'saved'
              : 'pending',
          saveError: storageFailure,
        })
      }
    } catch (error) {
      const message = storageMessage(error)
      queuedSessions.delete(id)
      if (!isDeleting(id)) reportFailure(id, message)
      else if (error instanceof DurableWriteError && error.committed && !forgottenElsewhere(id)) {
        // Committed, and then this tab's OWN record moved under it: the save
        // landed. Whether that matters is the deletion's to say — superseded if
        // it is accepted; if it is refused, a save like any other, so it is held
        // as a body and saved once more through the path that does a save's
        // bookkeeping (the row, a failure it ends). The shelf's version is learned
        // now, so that save expects what is actually there.
        committedVersions.set(id, doc.savedAt)
        heldBodies.add(id)
      }
      // Held, not reported, while this tab is deleting it — see `deleting`.
      else heldFailures.set(id, message)
    } finally {
      if (scheduledVersions.get(id) === doc.savedAt) scheduledVersions.delete(id)
    }
  })
  return writes.then(
    () => !failedDocuments.has(id) && (committedVersions.get(id) ?? -1) >= doc.savedAt,
  )
}

function queueState(s: StoreState): void {
  // A seed and its pending op are already an investigation. The pre-dispatch
  // flush must retain that intent before the first model can charge anything.
  if (s.session.order.length === 0 || queuedSessions.get(s.session.id) === s.session) return
  queuedSessions.set(s.session.id, s.session)
  queueDocument({
    v: SCHEMA,
    savedAt: Date.now(),
    session: s.session,
    ui: { selectedId: s.selectedId, focusId: s.focusId, lens: s.lens, temper: s.temper },
  })
}

/** Await this before closing in automation and before every priced adapter call.
 * pagehide is merely a best-effort checkpoint; it cannot promise an async commit. */
export async function flushDurablePersistence(): Promise<void> {
  queueState(useStore.getState())
  await writes
  if (storageFailure || recoverable.size)
    throw new ModelError(storageFailure ?? 'Work has not finished saving.', 'storage')
}

/**
 * Wait for every save already queued to finish, however it ends — the near side
 * of a deletion (`core/forget.ts`).
 *
 * A Forget used to land while the outgoing document's last checkpoint was still
 * queued, and the queue then read that checkpoint as superseded by this tab's own
 * deletion and dropped it. Harmless while a deletion could not fail. Once a
 * deletion can be refused, the exploration it leaves standing has to be the one
 * the reader last saw, so the queue settles first and the deletion is the newer
 * transaction. It never throws: a save that failed is still in `recoverable`, and
 * whether to delete what it was saving is the deletion's question, not this one's.
 */
export function settleDurableWrites(): Promise<void> {
  return writes.then(
    () => undefined,
    () => undefined,
  )
}

/**
 * A deletion the shelf ACCEPTED takes this tab's recovery copies of what it
 * deleted with it — `null` for everything. A recovery copy of a forgotten
 * exploration is a body the next retry would offer the shelf again, to be refused
 * by its tombstone and reported as a save failure about something the reader
 * asked to be rid of. Called only after the acceptance; on a refusal they are the
 * very copies "retain on refusal" means.
 */
function releaseForgotten(id: string | null): void {
  for (const key of [...recoverable.keys()]) if (id === null || key === id) recoverable.delete(key)
  for (const key of [...unwrittenVersions.keys()])
    if (id === null || key === id) unwrittenVersions.delete(key)
  for (const key of [...failedDocuments.keys()])
    if (id === null || key === id) failedDocuments.delete(key)
  // A shelf that failed to READ is not a failure about any one document, and no
  // deletion answers it: it stands until a retry reads the shelf.
  if (!hydrationComplete) return
  storageFailure = failedDocuments.values().next().value ?? null
  // A failure that was only ever about what is now gone has nothing left to say,
  // and left standing it would go on pausing paid work over it.
  const live = useStore.getState()
  if (live.saveState === 'failed' && storageFailure === null)
    useStore.setState({ saveState: 'idle', saveError: null })
  else if (live.saveState === 'failed') useStore.setState({ saveError: storageFailure })
}

/**
 * Open an exploration again after a refused deletion cleared it from the canvas
 * (`core/forget.ts`). Behind the queue — the refusal has just re-queued what it
 * held, and the open has to read what those saves leave — and only if the caller
 * still wants it once they have: the reader may have started something on the
 * cleared canvas in the time they took, and an open landing over that would
 * empty it.
 *
 * A body those saves could not write is what the reader last saw, and newer than
 * anything on the shelf, so it is the one put back — still unsaved, still said to
 * be, and still in the recovery copy. Opening the shelf's copy instead read an
 * older version onto the canvas the refusal promised to leave as it was, with the
 * newest edit only in an export the reader had no reason to look for. It is not
 * queued again here: its failure has already been said, and Retry saves it.
 */
export async function reopenKept(id: string, stillWanted: () => boolean): Promise<void> {
  await settleDurableWrites()
  if (!stillWanted()) return
  const unsaved = recoverable.get(id)
  if (unsaved) {
    if (!useStore.getState().yieldUnpaid()) return
    queuedSessions.set(id, unsaved.session)
    useStore.getState().restoreSession(unsaved)
    return
  }
  // The shelf's copy is read and activated before it lands, and the reader can
  // start something in that time too, so the open asks again after each.
  if (durableWritesEnabled()) await openDurableSession(id, stillWanted)
  else openSession(id)
}

export async function retryDurablePersistence(): Promise<void> {
  if (!hydrationComplete) {
    await initializeDurablePersistence()
    if (storageFailure) throw new ModelError(storageFailure, 'storage')
    return
  }
  for (const doc of recoverable.values()) queueDocument(doc)
  await flushDurablePersistence()
}

/** Includes every unsaved outgoing document as well as the current in-memory
 * exploration. Export does not require a healthy database. */
export function recoveryDocumentsJson(): string {
  const docs = new Map([...recoveryForks, ...recoverable])
  const checkpoint = persistedCheckpointRecovery()
  if (checkpoint) docs.set(`checkpoint:${checkpoint.session.id}`, checkpoint)
  const s = useStore.getState()
  if (s.session.order.length > 0)
    docs.set(s.session.id, {
      v: SCHEMA,
      savedAt: Date.now(),
      session: s.session,
      ui: { selectedId: s.selectedId, focusId: s.focusId, lens: s.lens, temper: s.temper },
    })
  return JSON.stringify({
    format: 'pathways-recovery',
    version: 1,
    documents: [...docs.values()].map((doc) => JSON.parse(storedSessionJson(doc))),
  })
}

/** Hydrate before mounting React: the demo must not race the active document.
 * Every legacy copy remains until an awaited transaction, exact readback and
 * index publication all succeed. An interrupted migration therefore resumes. */
export async function initializeDurablePersistence(): Promise<void> {
  const retrying = storageFailure !== null
  const retainWorkingCopy = () => {
    if (!retrying) return
    const live = useStore.getState()
    if (live.session.order.length === 0) return
    // Retrying may recover a different native boot target. Preserve both the
    // current scratch work and any edits made while its read was in flight;
    // an explicit storage retry is not permission to discard either.
    recoveryForks.set(`hydration:${live.session.id}`, {
      v: SCHEMA,
      savedAt: Date.now(),
      session: live.session,
      ui: {
        selectedId: live.selectedId,
        focusId: live.focusId,
        lens: live.lens,
        temper: live.temper,
      },
    })
  }
  retainWorkingCopy()
  hydrationComplete = false
  useStore.setState({ saveState: 'hydrating', saveError: null })
  setPaidCallDurabilityGuard(async () => {
    if (storageFailure) throw new ModelError(storageFailure, 'storage')
    await flushDurablePersistence()
  })
  try {
    if (!documentsHydrated() || storageHydrationFailure()) await hydrateStorage()
    if (storageHydrationFailure()) throw storageHydrationFailure()
    const active = (await readDurableActiveId()) ?? activeDocumentId()
    const ids = new Set([...docIds(), ...(await durableDocumentIds())])
    if (active) ids.add(active)
    for (const id of ids) {
      if (forgottenElsewhere(id)) continue
      let version = docVersion(id)
      let text: string | null
      if (version !== null) {
        // main.tsx already hydrated the packed shelf before importing the store.
        // Its versions are the bases for future conditional writes; unopened
        // documents need neither another IndexedDB read nor another decode,
        // compression and index publication before the first React render.
        committedVersions.set(id, version)
        if (id !== active && id !== DEMO_SESSION_ID) continue
        const packed = readDocText(id)
        text = packed === null ? null : unpackText(packed)
      } else {
        // Another tab may have added a document since that snapshot. Discover
        // it without rereading the documents whose versions we already hold.
        const record = await readDurableRecord(id)
        if (!record) continue
        version = record.version
        text = record.text
      }
      const doc = text === null ? null : readStoredSessionJson(text)
      if (!doc) continue
      committedVersions.set(id, version)
      workingBirths.set(id, doc.session.createdAt)
      if (docVersion(id) === null) publishDurableDocument(doc, text!, version)
      if (id === active) {
        // Activation checks the native version, so a newer write or deletion
        // after hydration refuses instead of restoring a stale active copy.
        await activateDurableDocument(id, version)
        retainWorkingCopy()
        useStore.getState().restoreSession(doc)
      }
    }
    storageFailure = null
    hydrationComplete = true
    useStore.setState({ saveState: 'idle', saveError: null })
    const checkpointWarning = checkpointRecoveryMessage()
    if (checkpointWarning) useStore.getState().toast(checkpointWarning, 'warn')
  } catch (error) {
    storageFailure = storageMessage(error)
    useStore.setState({ saveState: 'failed', saveError: storageFailure })
  }
  setDurableWriter(queueDocument)
}

/** Test isolation and application teardown, never a data deletion. */
export function resetDurablePersistence(): void {
  setDurableWriter(null)
  setPaidCallDurabilityGuard(null)
  recoverable.clear()
  failedDocuments.clear()
  deleting.clear()
  heldBodies.clear()
  heldFailures.clear()
  goneMeanwhile.clear()
  unwrittenVersions.clear()
  committedVersions.clear()
  scheduledVersions.clear()
  readEpochs.clear()
  recoveryForks.clear()
  workingBirths.clear()
  queuedSessions.clear()
  storageFailure = null
  hydrationComplete = false
  writes = Promise.resolve()
}

/**
 * The storage-full sentence, once per session — a toast on every keystroke would
 * be its own failure — and re-armed by the first write that lands. Silence here
 * is the worst outcome in the file: the user keeps working, the save keeps
 * failing, and the next reload throws the session away with no explanation.
 *
 * Reached two ways. From `writeState` above when the index row is refused, and
 * from the shelf's answer to a document write, which arrives AFTER `writeDoc`
 * has returned — the shelf is asynchronous — through `onWriteRefused`, which
 * `installPersistence` subscribes to below.
 */
function sayRefused(s: StoreState) {
  if (refused) return
  // With no shelf the page has already been told it keeps nothing, and this
  // sentence would send them to free space they are not short of.
  if (!shelfOpen()) return
  refused = true
  // To the library, where each exploration is listed with its size and its own
  // confirmed Forget — not to the palette's Forget everything, which sent a
  // reader under storage pressure to the one door that deletes the lot (D335).
  s.toast(
    'This browser refused to save — its storage is full. Nothing since the last save was kept. Forget the explorations you no longer need in the Library to free space.',
    'bad',
    { label: 'Open Library', run: () => s.setLibrary(true) },
  )
}
/**
 * The version a patch of a stored document takes, and never the one it read.
 *
 * STAMPED, not inherited, and stamped so it is always GREATER. The shelf's
 * `putIfNewer` takes `savedAt` as the version and writes only on a strictly
 * greater one, so a document already on the shelf at this version refuses the
 * write that follows and keeps the older bytes. Carrying the stamp that was read
 * made the patch a write that landed nothing; `Date.now()` alone left the same
 * outcome for a patch landing inside the same millisecond as the save before it,
 * or on a clock that has gone backwards. The wall clock is a reading, not a
 * counter, so it is only ever the floor here.
 *
 * One function because two writers below need the same number for the same
 * reason, and a rule implemented at one of the two places it applies is the
 * shape this file has already been bitten by.
 */
function restamp(doc: StoredSession): number {
  return Math.max(Date.now(), doc.savedAt + 1)
}

/**
 * Write a document the reader is NOT looking at.
 *
 * The same door as the autosave's, kept as a function because the two writers
 * below share the reason it exists: a receipt or a label for an exploration the
 * reader has left is a SAVE of that document, and goes through `writeDoc` with
 * the forget guard and the shelf's version ordering like every other one.
 */
function writeAside(next: StoredSession): boolean {
  return writeDoc(next).ok
}

/**
 * Record spend against an exploration that is NOT the one on screen.
 *
 * One channel in this app can outlive the exploration it was asked about. Every
 * reasoning pass holds `busy`, and `openSession` refuses while `busy` is set, so
 * a run and its ledger cannot come apart. Asking deliberately takes no lock —
 * that is the point of the channel — so the reader can open something else while
 * an answer is arriving, and the tokens land after the ledger they belong to has
 * been put away. They used to land on whatever was open instead.
 *
 * It lives HERE rather than in `persist.ts` because it has to know which
 * exploration is OPEN — that is what decides whether this door is the right one —
 * and `persist.ts` must never import the store. See D38.
 *
 * `false` means the document is not there to write to, and the caller must not
 * treat that as licence to put the row somewhere else: a forgotten exploration
 * took its ledger with it, and there is nothing left for the figure to be an
 * estimate OF. Losing the row is the honest outcome; moving it is not.
 *
 * Not for the open exploration — that one is the store's, held in memory, and
 * writing it from here would race the autosave that owns it.
 */
export function recordNarrationOn(sessionId: string, operation: Op, born: number): boolean {
  const doc = loadSession(sessionId)
  if (!doc || doc.session.createdAt !== born) return false
  const session = retainNarration(doc.session, operation)
  return !!session && writeAside({ ...doc, savedAt: restamp(doc), session })
}

export function recordSpendOn(sessionId: string, row: Spend, born: number): boolean {
  // The same refusal the open ledger makes, and it has to be made here too: a
  // request that failed before any usage was reported still carries a Spend, and
  // an all-zero row written into a stored document is a claim that a model ran.
  // Written once in `spentNothing` so the two writers cannot come to disagree.
  if (spentNothing(row)) return false
  const doc = loadSession(sessionId)
  if (!doc) return false
  // The SAME question `spendGoesTo` asks about the open document, asked here
  // about the stored one — because this path is reached by the ids DIFFERING,
  // and an id is not a document. `s_demo` is reused, so the document now under
  // that name can be a replayed worked example that owes nothing: the instance
  // this receipt belongs to was forgotten, and writing the row here would
  // attribute a deleted exploration's spend, with its model stamp, to an
  // unrelated one.
  //
  // `born` is required rather than optional on purpose. The open branch was
  // guarded first and this one was left comparing names, which is a rule
  // implemented at one of the two places it applies — the shape this branch has
  // now been bitten by three times. A caller that cannot say which document it
  // paid for has no business writing to one.
  //
  // Exact equality, and it is safe only because the codec reads `createdAt` as
  // written — `storedIdentity` in `persist.ts`. Translated with the other
  // clocks, it moved by a millisecond for every millisecond between the read
  // that minted `born` and this one whenever the save clock was ahead, and this
  // line refused the document's own receipt (D79).
  if (doc.session.createdAt !== born) return false
  const next: StoredSession = {
    // Stamped through the one stamp, for the reason it gives. It matters most
    // here of the two: the stamp is the shelf's version, and a receipt written
    // under the number the document already had could lose to a save still in
    // flight, leaving the ledger reading LOW — the one direction it must never
    // read.
    ...doc,
    savedAt: restamp(doc),
    session: { ...doc.session, spend: mergeSpend(doc.session.spend, row) },
  }
  return writeAside(next)
}

/**
 * File a STORED exploration under a project, or pass null to unfile it. Returns
 * whether the label is now what was asked for.
 *
 * The other door is `setProject`, for the exploration that is OPEN, and the
 * split is the same one `duplicateSession`/`duplicateStored` make one door over:
 * the open session is the store's, held in memory and written by the autosave on
 * object identity. Filing it from here would put a label on disk that the next
 * debounce overwrites with the in-memory session that never carried it — the
 * label would appear, the row would regroup, and a second later it would be gone
 * with nothing on screen saying so. The caller branches; see the trap comment at
 * the branch point in `ExplorationsSidebar`. Refused here as well rather than
 * only documented, because a rule implemented at one of the two places it
 * applies is the shape this file keeps being bitten by.
 *
 * Normalised through `projectLabel` — the ONE clamp, the same one `setProject`,
 * both fork doors and the codec come through — so a label of spaces unfiles
 * rather than opening a group with a blank header.
 *
 * A no-op is a SUCCESS. Asking for the label a document already carries has
 * nothing to write, and answering `false` would have the caller report a failure
 * over a document that is filed exactly as requested. Nothing is written in that
 * case at all.
 *
 * `false` is a refusal and never a throw: an id nothing is stored under, an id
 * whose document another tab forgot while this pane was listing it, or a write
 * the browser would not take. The row stays where it was in every one of them.
 */
export function fileStored(id: string, project: string | null): boolean {
  // Never the open one. See the header — and note the shape of this guard: it
  // asks the STORE, not the caller, so it holds however the id reached here.
  //
  // PER-TAB, AND IT CANNOT BE ANYTHING ELSE: this tab knows what this tab has
  // open and nothing about what any other tab is holding, so the same press made
  // next door is a legitimate write onto a document somebody here is looking at.
  // That half is answered by ADOPTION rather than by a guard — see
  // `projectAdoption` in `persist.ts`, installed by `installPersistence` above.
  if (id === useStore.getState().session.id) return false
  const label = projectLabel(project)
  // Read before anything is decided, which is also where the forget question is
  // asked first: `loadSession` takes this tab's baseline before the read and
  // re-asks after the parse, so a document another tab deleted is gone rather
  // than filed. Absence is the honest answer here — there is no document for the
  // label to be about — and the caller must not read it as licence to put the
  // row somewhere else.
  const doc = loadSession(id)
  if (!doc) return false
  const already = projectLabel(doc.session.project)
  // Already where it was asked to go. Not a write, and not a failure either.
  if (already === label) return true
  const next: StoredSession = {
    ...doc,
    savedAt: restamp(doc),
    session: {
      ...doc.session,
      // Absence is the single representation of "no project" (`projectLabel`),
      // so unfiling DELETES the field rather than storing an empty string — a
      // blank label would group apart from the documents written before this
      // field meant anything.
      ...(label ? { project: label } : { project: undefined }),
    },
  }
  // And the write itself is the ordinary one for a document the reader is not
  // looking at, forget guard and all: `writeDoc` refuses outright when this
  // tab's baseline has moved, and unmakes its own commit when the forget lands
  // inside the window where it was serialising. A label is not worth
  // resurrecting a deleted exploration for — see `core/forgotten.ts`.
  return writeAside(next)
}

/**
 * Name a STORED exploration, or pass null to take its name away. Returns whether
 * the name is now what was asked for.
 *
 * `fileStored`'s twin, split from `setName` for `fileStored`'s reason and guarded
 * the same way: the open exploration lives in the store and is written by the
 * autosave on object identity, so a name written to its document from here would
 * be overwritten by the next save of a session that never carried it. Normalised
 * through `nameFor`, so a name of spaces, or the seed's own words, is absence —
 * and asking for what is already there writes nothing and answers true.
 *
 * Only the name moves. The seed, the graph and every record in the document are
 * written back exactly as they were read.
 */
export function nameStored(id: string, name: string | null): boolean {
  if (id === useStore.getState().session.id) return false
  const doc = loadSession(id)
  if (!doc) return false
  const next = nameFor(name, doc.session.seed)
  if (next === nameFor(doc.session.name, doc.session.seed)) return true
  return writeAside({
    ...doc,
    savedAt: restamp(doc),
    session: { ...doc.session, ...(next ? { name: next } : { name: undefined }) },
  })
}

/** One refusal notice per session. Reset by the first write that succeeds. */
let refused = false

/**
 * The sentence for unavailable or unreadable storage. A failed read says
 * nothing about whether earlier saved documents still exist. Said once, when
 * persistence is installed. Working copies remain readable and exportable,
 * but a save cannot be reported as committed and paid calls remain blocked
 * until an explicit retry successfully reads and writes the shelf.
 */
export const NOTHING_KEPT =
  'This browser could not open its storage. Changes on screen are held in memory; export a copy before closing this tab, or retry saving.'

export function installPersistence(): () => void {
  if (!shelfOpen()) useStore.getState().toast(NOTHING_KEPT, 'bad')
  let timer = 0
  let firstPending = 0

  const cancelTimers = () => {
    if (timer) {
      clearTimeout(timer)
      timer = 0
    }
    firstPending = 0
  }
  const flush = () => {
    cancelTimers()
    writeState(useStore.getState())
  }
  // The pagehide flush, and the one write that asks for the net: the shelf is
  // asynchronous, and a page on its way out may not live long enough for the
  // commit. See `writeDoc`.
  const flushLeaving = () => {
    cancelTimers()
    writeState(useStore.getState(), { checkpoint: true })
  }
  const schedule = () => {
    const now = Date.now()
    if (!firstPending) firstPending = now
    if (timer) clearTimeout(timer)
    const wait = Math.min(DEBOUNCE_MS, Math.max(0, firstPending + CEILING_MS - now))
    timer = window.setTimeout(flush, wait)
  }

  const unsub = useStore.subscribe((s, prev) => {
    if (s.session.id !== prev.session.id) {
      // The whole safety story for the destructive paths. Starting over,
      // clearing, restoring and replaying the demo each install a session with
      // a new id, so all of them checkpoint the outgoing exploration through
      // this one hook — with no edit to any action and no confirm dialog.
      cancelTimers()
      writeState(prev)
      writeState(s)
      // The incoming exploration was just opened, and the dot beside its row
      // is an unread mark that opening takes down (D103, D334): one stamp here, for
      // every door — a restore, a fresh start, the demo, a fork — rather than
      // one in each. It re-enters this subscriber with the same session, which
      // is a prefs write and nothing else.
      useStore.getState().markOpened(s.session.id)
      return
    }
    if (s.session !== prev.session) {
      if (durableWritesEnabled() && s.saveState !== 'failed')
        useStore.setState({ saveState: 'pending' })
      schedule()
    }
    if (prev.busy && !s.busy) flush()
    if (prefsChanged(s, prev)) writePrefs(toPrefs(s))
  })

  const onHide = () => {
    if (document.visibilityState === 'hidden') flush()
  }
  document.addEventListener('visibilitychange', onHide)
  window.addEventListener('pagehide', flushLeaving)

  /* ADOPT A LABEL ANOTHER TAB PUT ON THE EXPLORATION THIS ONE IS HOLDING.
     `projectAdoption` carries the whole argument, including why a guard could
     not have done this: `fileStored` refuses the session THIS tab has open, and
     no tab can know what another tab is holding, so without adoption a filed
     exploration open elsewhere is unfiled again by that tab's next save — or by
     it closing. Here rather than in `persist.ts` because this is the layer that
     may touch the store, and inside `installPersistence` because the listener
     has to come down with everything else this function put up.

     Setting the session replaces the object, so the subscription above schedules
     a save and the tab's own copy stops disagreeing with disk. It cannot ring
     back and forth: the adoption is a no-op once the labels match, which they do
     the moment it lands. No history step and no `rev` — this is not an act
     somebody took in this tab, and a graph nobody changed must not redraw. */
  const onStorage = (e: StorageEvent) => {
    // The cross-tab half of the shelf first: a save next door announces itself
    // through the index row it wrote, and the copy this tab holds is refreshed
    // to it. See `refreshFromIndexEvent`.
    refreshFromIndexEvent(e)
    const s = useStore.getState().session
    const adopt = projectAdoption(e, s)
    if (adopt) {
      useStore.setState({
        session: { ...s, ...(adopt.project ? { project: adopt.project } : { project: undefined }) },
      })
    }
    // The name, the one other field a second surface writes (`nameStored`), on
    // the same event and by the same argument — `nameAdoption` carries it. Read
    // again rather than reusing `s`, which the project's adoption may have just
    // replaced.
    const t = useStore.getState().session
    const named = nameAdoption(e, t)
    if (!named) return
    useStore.setState({
      session: { ...t, ...(named.name ? { name: named.name } : { name: undefined }) },
    })
  }
  window.addEventListener('storage', onStorage)

  // The shelf answers a document write after `writeDoc` has returned, so a
  // refusal is heard here rather than read off the result — once per session,
  // through the same sentence a refused index row gets.
  const unhear = onWriteRefused(() => sayRefused(useStore.getState()))

  return () => {
    unsub()
    document.removeEventListener('visibilitychange', onHide)
    window.removeEventListener('pagehide', flushLeaving)
    window.removeEventListener('storage', onStorage)
    unhear()
    cancelTimers()
  }
}

/** What a live pass gets from every door in this file that would swap the session
 *  under it. */
const BUSY_REFUSAL = 'One thought at a time — let the current branch finish.'

/**
 * Open a stored exploration, optionally landing on one node inside it.
 *
 * `nodeId` exists for the cross-exploration candidate list: "go to the other
 * side" has to arrive at the node itself, and a restore that dropped you at that
 * exploration's seed would leave you to find it again by hand.
 */
export function openSession(id: string, nodeId?: string) {
  const st = useStore.getState()
  // The worked example's unfurl is stopped rather than waited for: it spends
  // nothing, and its op is ended on its own document before this goes on, so
  // the save queued below is of a finished demo (`yieldUnpaid`).
  if (!st.yieldUnpaid()) {
    st.toast(BUSY_REFUSAL, 'warn')
    return
  }
  // Already open: land on the node and read nothing. `loadSession` reads the
  // SHELF, and autosave is debounced — so restoring that copy over the live
  // session discards every edit since the last write, and a refused save leaves
  // that window open with no end to it. Nothing here is a switch, so there is
  // nothing to load.
  //
  // `Breakthroughs`'s `goTo` has always drawn this line by hand, branching on
  // whether an end is 'here' or 'elsewhere'; the sidebar draws it too, with
  // `if (e.id !== sessionId)`. The due list, the library and the palette did
  // not, so it belongs at the door rather than in a fourth copy.
  //
  // After the busy refusal, not before it: whether a pass in flight should stop
  // somebody selecting a node in the exploration already on screen is a separate
  // question, and answering it here would change what this door refuses. Before
  // the durable-writes door below, for the same reason it is before `loadSession`:
  // `openArchived` would read the shelved copy over the live session too.
  if (st.session.id === id) {
    if (nodeId && st.session.nodes[nodeId]) {
      useStore.getState().revealNode(nodeId)
      useStore.getState().select(nodeId)
    }
    return
  }
  if (durableWritesEnabled()) {
    void writes
      .then(() => openArchived(id))
      .then((result) => {
        if (result === 'ok' && nodeId && useStore.getState().session.nodes[nodeId]) {
          useStore.getState().revealNode(nodeId)
          useStore.getState().select(nodeId)
        }
      })
    return
  }
  const doc = loadSession(id)
  if (!doc) {
    st.toast('That exploration could not be read back.', 'bad')
    return
  }
  const landOn = nodeId && doc.session.nodes[nodeId] ? nodeId : null
  useStore
    .getState()
    .restoreSession(landOn ? { ...doc, ui: { ...doc.ui, selectedId: landOn } } : doc)
  // After the restore, not inside it: `restoreSession` resets the fold state from
  // the document, so unfolding has to come second or it is immediately undone.
  if (landOn) {
    useStore.getState().revealNode(landOn)
    useStore.getState().select(landOn)
  }
}

/** Compatibility name for a fresh read from the one document shelf. */
export function openArchived(
  id: string,
): Promise<'ok' | 'unreadable' | 'missing' | 'unreachable' | 'busy' | 'superseded' | 'stale'> {
  return openDurableSession(id)
}

/** Durable opens ask the database, so a document created in another tab and a
 * browser that refused every localStorage manifest still open normally.
 *
 * `wanted`, from a reopen after a refused deletion (`reopenKept`), is asked again
 * after each wait, ahead of everything else: an open it no longer wants gives up
 * as superseded and says nothing, since the caller has decided what happens to it
 * — owed to a deletion of it pressed meanwhile, or dropped for words typed on the
 * canvas it would have replaced. */
async function openDurableSession(
  id: string,
  wanted: () => boolean = () => true,
): ReturnType<typeof openArchived> {
  // Asked BEFORE the baseline below is taken: stopping the unfurl ends its op,
  // which is a new session object, and a baseline taken first would read that
  // as somebody else's navigation and give the open up as superseded.
  if (!useStore.getState().yieldUnpaid()) {
    useStore.getState().toast(BUSY_REFUSAL, 'warn')
    return 'busy'
  }
  const before = useStore.getState()
  const generation = markNow(id)
  try {
    const record = await readDurableRecord(id)
    if (!wanted()) return 'superseded'
    const current = useStore.getState()
    if (current.busy) return 'busy'
    if (markMoved(id, generation) || current.session !== before.session) return 'superseded'
    if (!record) {
      current.toast('That exploration is no longer stored.', 'bad')
      return 'missing'
    }
    const doc = readStoredSessionJson(record.text)
    if (!doc) {
      current.toast('That exploration could not be read back.', 'bad')
      return 'unreadable'
    }
    // Activation is itself a compare-and-set: a newer save or a deletion during
    // parsing leaves both the live document and the boot pointer untouched.
    await activateDurableDocument(id, record.version)
    if (!wanted()) return 'superseded'
    if (useStore.getState().busy) return 'busy'
    if (markMoved(id, generation) || useStore.getState().session !== before.session)
      return 'superseded'
    const unsaved = recoverable.get(id)
    if (unsaved) recoveryForks.set(`${id}:${Date.now()}:${recoveryForks.size}`, unsaved)
    if (
      before.session.id === id &&
      before.session.order.length > 0 &&
      unsaved?.session !== before.session
    ) {
      // An explicit conflict reopen may deliberately show the newer shelf copy.
      // Preserve the actual working body first, including a last edit whose
      // debounce has not queued it yet. Edits made during either await above
      // supersede this open entirely and remain live instead.
      recoveryForks.set(`${id}:${Date.now()}:${recoveryForks.size}`, {
        v: SCHEMA,
        savedAt: Date.now(),
        session: before.session,
        ui: {
          selectedId: before.selectedId,
          focusId: before.focusId,
          lens: before.lens,
          temper: before.temper,
        },
      })
    }
    recoverable.delete(id)
    readEpochs.set(id, (readEpochs.get(id) ?? 0) + 1)
    scheduledVersions.delete(id)
    committedVersions.set(id, record.version)
    publishDurableDocument(doc, record.text, record.version)
    queuedSessions.delete(id)
    failedDocuments.delete(id)
    storageFailure = failedDocuments.values().next().value ?? null
    useStore.getState().restoreSession(doc)
    useStore.setState({ saveState: storageFailure ? 'failed' : 'saved', saveError: storageFailure })
    return 'ok'
  } catch (error) {
    if (!wanted()) return 'superseded'
    useStore.getState().toast(storageMessage(error), 'bad')
    return error instanceof DurableWriteError && error.reason === 'conflict'
      ? 'stale'
      : 'unreachable'
  }
}

/**
 * Copy a STORED exploration, without opening either one. Returns the new id, or
 * null when there was nothing to read or nowhere to put it — and on that null
 * the source is exactly as it was found, filing included.
 *
 * The other door is `duplicateSession`, for the exploration that is open. Two
 * doors because the two acts are different at the storage boundary and not in
 * the store: this one never touches the live session, so a copy taken from the
 * sidebar's list does not throw away what is on the canvas. Switching to the
 * copy is the CALLER's decision, made with `openSession` and its live-run guard.
 *
 * The copy is minted by `sharedExplorationCopy` — the same function a received
 * share link goes through, rather than a second literal that would eventually
 * carry one field the other did not. Everything in the document travels
 * verbatim, so no verification is re-gated and no node is re-stamped.
 */
export function duplicateStored(id: string): string | null {
  const doc = loadSession(id)
  if (!doc) return null
  const s = doc.session
  // The same naming rule `duplicateSession` applies to the open session, through
  // the same function: a filed source hands its label to the copy, an unfiled one
  // is named after its own seed on BOTH sides so the pair reads as a family.
  const already = projectLabel(s.project)
  const project = already ?? forkFamilyLabel(s.seed)
  const filed: StoredSession = already ? doc : { ...doc, session: { ...s, project } }

  // THE COPY GOES DOWN FIRST. Filing the source is a write of its own — it moves
  // the row the user pressed into a family — and doing it before the copy landed
  // meant a refused fork regrouped the source anyway, quietly and for a sibling
  // that does not exist. The source is only ever touched once the copy is real.
  const minted = sharedExplorationCopy(filed)
  // What `duplicateSession` writes on the open door's copy, written here on the
  // stored door's: a copy of the whole graph, of this source, today. Added on
  // top of the minted copy rather than inside `sharedExplorationCopy`, which a
  // received link and an opened file also go through, and neither is a fork.
  const copy: StoredSession = {
    ...minted,
    session: {
      ...minted.session,
      forkedFrom: { from: s.id, kind: 'copy', at: minted.savedAt },
    },
  }
  const wrote = writeDoc(copy)
  if (wrote.ok && !already) {
    // Re-read rather than reused, and STAMPED, because this write IS a save of
    // the source: `savedAt` is the shelf's version as well as the library's
    // clock, so writing the source back under the number it already had would
    // put the pre-filing bytes and the filed bytes in front of `putIfNewer`
    // claiming the same save. Through `restamp` because the wall clock alone
    // leaves that same outcome for a fork taken inside the same millisecond as
    // the source's last save.
    const fresh = loadSession(id)
    if (fresh) {
      writeDoc({ ...fresh, savedAt: restamp(fresh), session: { ...fresh.session, project } })
    }
  }
  // A refused write leaves no copy to open, and returning its id anyway would
  // send the caller to `openSession` for a document that is not there. The READ
  // BACK is the belt to that brace: the id handed out here is a promise the
  // sidebar acts on immediately, so it is worth confirming rather than deducing.
  const landed = wrote.ok && loadSession(copy.session.id) !== null
  return landed ? copy.session.id : null
}

/**
 * Install what a Pathways file carried (`readExplorationFile`), each exploration
 * as a NEW, independent document. Returns the one opened and how many others
 * were added beside it, or null when a pass holds the canvas.
 *
 * Every copy is minted by `sharedExplorationCopy`, the function a received share
 * link goes through, so a file's own id never lands here. A file exported from
 * this browser opens as a second exploration beside the one it came from, never
 * over it, and opening the same file twice makes two. The newest document is
 * opened exactly as a link is — `restoreSession`, and the autosave's id-change
 * hook writes it and checkpoints what was on screen. A recovery copy can hold
 * several (every outgoing exploration whose save had not landed); the others
 * are written beside it through `writeDoc` without being opened, the way
 * `duplicateStored` writes its copy, so nothing the file held is dropped.
 */
export function openExplorationFile(
  docs: readonly StoredSession[],
): { opened: StoredSession; added: number } | null {
  const st = useStore.getState()
  if (st.busy) {
    st.toast(BUSY_REFUSAL, 'warn')
    return null
  }
  if (!docs.length) return null
  const newest = docs.reduce((a, b) => (b.savedAt > a.savedAt ? b : a))
  let added = 0
  for (const doc of docs) {
    if (doc !== newest && writeDoc(sharedExplorationCopy(doc)).ok) added += 1
  }
  const opened = sharedExplorationCopy(newest)
  useStore.getState().restoreSession(opened)
  return { opened, added }
}

export { DEMO_SESSION_ID }
